import { useState, FC, useEffect } from 'react';
import { SearchResult, type PLP_TYPE } from '@commerce-search-webcomponents/ui-composite';
import {
  searchApi,
  SearchType,
  BucketField,
  NO_RESULT_FILTERS,
  type ProductSearch,
  type Bucket,
  type Filter,
} from '@commerce-search-webcomponents/sdk';
import { BannerComponent } from '@commerce-product-webcomponents/ui';
import { useSession } from '@nextgen-web-framework/all';
import { ProjectionType } from '@commerce-product-webcomponents/sdk';
import type { SearchState, ProductListingContainerProps } from './types/propsTypes';

const filterInitialState = {
  sort: '',
  brand: [],
  category: '',
  solution: '',
  price: '',
};

const ProductListingContainer: FC<ProductListingContainerProps> = (
  props: ProductListingContainerProps,
): JSX.Element => {
  const {
    goToShop,
    searchedKeyword,
    link,
    alt,
    cssCode,
    bannerTitle,
    updatedSearchResults,
    onProductData,
    searchSourceKey = 'categories',
  } = props;

  const [plpState, setPlpState] = useState<SearchState>({
    offset: 0,
    limit: 8,
    isLoading: false,
    facets: [],
    filter: filterInitialState,
    fulltextSearchResults: {},
    availableQuickFilters: [],
    quickFilter: '',
    fulltextSearchError: false,
    noResultFilterName: '',
  });

  const [payload, setPayload] = useState<string[][]>([]);
  const {
    session: { warehouseId = '' },
  } = useSession();

  useEffect(() => {
    if (payload.length > 0) {
      fetchData();
    }
  }, [payload]);

  const resetSearchResult = () => {
    setPlpState((prev) => ({
      ...prev,
      facets: [],
      filter: filterInitialState,
      fulltextSearchResults: {},
      offset: 0,
      availableQuickFilters: [],
      quickFilter: '',
      fulltextSearchError: false,
      noResultFilterName: '',
    }));
  };

  const resetFilter = () => {
    setPlpState((prev) => ({
      ...prev,
      filter: filterInitialState,
    }));
  };

  const getFilterParams = (prevState: SearchState) => {
    const { filter, quickFilter } = prevState;
    const { price, brand, category, solution } = filter;
    const [min, max] = (price || '').split(' - ');
    const priceFilter = min || max ? `${min || ''};${max || ''}` : '';

    return [
      ...(priceFilter ? [['filter', `price:${priceFilter}`]] : []),
      ...(brand && brand.length ? [['filter', `brand:${brand.join(';')}`]] : []),
      ...(category ? [['filter', `category:${category}`]] : []),
      ...(solution ? [['filter', `solution:${solution}`]] : []),
      ...(quickFilter ? [['filter', `quickFilters:${quickFilter}`]] : []),
    ];
  };

  const createPayload = () => {
    setPlpState((prevState) => {
      const { offset, limit, filter } = prevState;
      const { sort } = filter;
      const filterParams = getFilterParams(prevState);

      const constraints: string[][] = Object.entries({
        keywords: `${searchSourceKey}:${searchedKeyword}`,
        searchType: SearchType.Fieldmatch,
        offset: `${offset}`,
        limit: `${limit}`,
        projections: ProjectionType.Listing,
        ...(warehouseId && { warehouseId }),
        ...(sort ? { sort } : {}),
      }).concat(filterParams as [string, string][]);

      setPayload(constraints);

      return { ...prevState, isLoading: true };
    });
  };

  const fetchData = () => {
    searchApi
      .getSearchResults({ payload })
      .then((result: ProductSearch) => {
        setPlpState((prev) => {
          let offset = result.offset ?? prev.offset;
          let { facets } = prev;
          let noResultFilterName = '';
          const isNoOffset = Number(offset) === 0;
          const { total } = result;

          if (isNoOffset) {
            onProductData(result.products || []);
          }

          if (total) {
            offset += prev.limit;
          }
          updatedSearchResults(total as number);

          const quickFilterBucket = result.buckets?.find(({ field }) => field === BucketField.QUICK_FILTERS);
          const availableQuickFilters = quickFilterBucket?.values?.map((ob) => (ob.count && ob.name) || '') || [];
          const filtersInScope = [BucketField.CATEGORY, BucketField.SOLUTION, BucketField.BRAND, BucketField.PRICE];

          if (total) {
            facets =
              result.buckets
                ?.filter((obj: Bucket) => filtersInScope.includes(obj.field as BucketField))
                .sort(
                  (f1, f2) =>
                    filtersInScope.indexOf(f1.field as BucketField) - filtersInScope.indexOf(f2.field as BucketField),
                ) || [];
          } else {
            noResultFilterName = prev.filter?.price != '' ? NO_RESULT_FILTERS.PRICE : '';
          }

          return {
            ...prev,
            fulltextSearchResults: result,
            offset,
            isLoading: false,
            facets,
            noResultFilterName,
            availableQuickFilters,
            fulltextSearchError: false,
          };
        });
      })
      .catch(() => {
        setPlpState((prev) => ({
          ...prev,
          isLoading: false,
          fulltextSearchError: true,
        }));
      });
  };

  return (
    <>
      <BannerComponent
        link={link}
        alt={alt}
        bannerTitle={bannerTitle}
        cssCode={cssCode}
        bannerLoading={!(Object.keys(plpState.fulltextSearchResults).length > 0 && !plpState.fulltextSearchError)}
      />
      <SearchResult
        isPLP
        goToShop={goToShop}
        searchedKeyword={searchedKeyword}
        offset={plpState.offset}
        limit={plpState.limit}
        isLoading={plpState.isLoading}
        fulltextSearchResults={plpState.fulltextSearchResults}
        facets={plpState.facets}
        filter={plpState.filter}
        updateLimit={(limit: number) => setPlpState((prev) => ({ ...prev, limit }))}
        updateOffset={(offset: number) => setPlpState((prev) => ({ ...prev, offset }))}
        resetSearchResult={resetSearchResult}
        fetchResults={createPayload}
        updateFilter={(filter: Filter) => setPlpState((prev) => ({ ...prev, filter }))}
        resetFilter={resetFilter}
        updateQuickFilter={(quickFilter: string) => setPlpState((prev) => ({ ...prev, quickFilter }))}
        availableQuickFilters={plpState.availableQuickFilters}
        noResultFilterName={plpState.noResultFilterName}
        quickFilter={plpState.quickFilter}
        originallySearchedText={searchedKeyword}
        plpType={searchSourceKey as PLP_TYPE}
        fulltextSearchError={plpState.fulltextSearchError}
      />
    </>
  );
};

export default ProductListingContainer;
export type { ProductListingContainerProps };
